import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import "./style.css";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  GetDashboardCounters,
  GetDashboardRegisterUserChart,
  GetPopularDiscovers,
} from "../../Api/dashboard";
import { ErrorToast } from "../../Utils/SweetAlert";
import noImage from "../../assets/Images/no-image.png";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRange } from "react-date-range";
import { Popover, Button } from "@mui/material";
import { format } from "date-fns";

const Dashboard = () => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );
  const [analyticsPeriod, setAnalyticsPeriod] = useState("month");
  const [anchorEl, setAnchorEl] = useState(null);
  const selectRef = useRef(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const handleChange = (event) => {
    const value = event.target.value;
    if (value === "custom") {
      setAnchorEl(selectRef.current);
      setAnalyticsPeriod("custom");
      setTempDateRange(dateRange); // Initialize temp range with current applied range
    } else {
      setAnalyticsPeriod(value);
      setAnchorEl(null);
    }
  };

  const handleDateSelect = (ranges) => {
    setTempDateRange([ranges.selection]);
  };

  const handleApplyCustomDate = () => {
    setDateRange(tempDateRange);
    setAnchorEl(null);
    // Refetch will be triggered by useEffect dependency on dateRange
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    // Optional: Reset temp range to applied range on close without applying?
    // setTempDateRange(dateRange); 
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;



  const { data: counters, isFetching: countersLoading } = useQuery({
    queryKey: ["get-counters-dashboard"],
    queryFn: () => GetDashboardCounters(),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false, // prevents stretching
    plugins: {
      legend: { position: "bottom", labels: { font: { size: 14 } } },
      title: { display: false },
    },
    scales: {
      x: { ticks: { font: { size: 12 } } },
      y: { ticks: { font: { size: 12 } } },
    },
  };

  const {
    data: chartData,
    isFetching: registerUserChartLoading,
    refetch: refetchChart,
  } = useQuery({
    queryKey: ["dashboard-register-user-chart"],
    queryKey: ["dashboard-register-user-chart"],
    queryFn: () => {
      let startDate = "";
      let endDate = "";
      if (analyticsPeriod === "custom") {
        startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
        endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
      }
      return GetDashboardRegisterUserChart(analyticsPeriod, startDate, endDate);
    },
    select: (response) => {
      let labels = [];
      let values = [];
      for (const item of response?.data) {
        labels.push(item?.label);
        values.push(item?.count);
      }

      return {
        labels,
        datasets: [
          {
            label: "Users",
            data: values,
            backgroundColor: "#AD5ADC",
          },
        ],
      };
    },
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  const { data: popularDiscovers, isFetching: discoverLoading } = useQuery({
    queryKey: ["get-dashboard-popular-discovers"],
    queryFn: () => GetPopularDiscovers(),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  const handleImageError = (event) => {
    event.target.src = noImage;
  };

  useEffect(() => {
    refetchChart();
  }, [analyticsPeriod, dateRange]);

  return (
    <React.Fragment>
      <Helmet>
        <title>Ove | Admin Dashboard</title>
      </Helmet>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-xl-4 col-md-6 col-sm-12">
              <Link to="/admin/client-user-listing" className="dashboard-card-text-link">
                <div className="card shadow-sm border-0 rounded-4 hover-card mb-3">
                  <div className="card-body d-flex align-items-center">
                    <span className="dash-widget-icon bg-pareto-theme-black me-3 rounded-circle d-flex justify-content-center align-items-center" style={{ width: 50, height: 50 }}>
                      <PeopleIcon fontSize="medium" className="text-white" />
                    </span>
                    <div>
                      <div className="dash-title dashboard-card-text-link">Total Users</div>
                      <div className="h4 mb-0">
                        {countersLoading ? "Loading..." : counters?.data?.client_user_count || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-12">
              <Link to="/admin/product-listing" className="dashboard-card-text-link">
                <div className="card shadow-sm border-0 rounded-4 hover-card mb-3">
                  <div className="card-body d-flex align-items-center">
                    <span className="dash-widget-icon bg-pareto-theme-black me-3 rounded-circle d-flex justify-content-center align-items-center" style={{ width: 50, height: 50 }}>
                      <InventoryIcon fontSize="medium" className="text-white" />
                    </span>
                    <div>
                      <div className="dash-title dashboard-card-text-link">Shop Products</div>
                      <div className="h4 mb-0">
                        {countersLoading ? "Loading..." : counters?.data?.product_count || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-12">
              <div className="card shadow-sm border-0 rounded-4 mb-3">
                <div className="card-body d-flex align-items-center">
                  <span className="dash-widget-icon bg-pareto-theme-black me-3 rounded-circle d-flex justify-content-center align-items-center" style={{ width: 50, height: 50 }}>
                    <HealthAndSafetyIcon fontSize="medium" className="text-white" />
                  </span>
                  <div>
                    <div className="dash-title dashboard-card-text-link">User cycles</div>
                    <div className="h4 mb-0">
                      {countersLoading ? "Loading..." : counters?.data?.user_cycle_count || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/admin-user-listing"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <ManageAccountsIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Admin Users
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h4">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.user_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/guide-listing"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <HelpIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Guide Products
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.guide_product_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/category-listing"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <CategoryIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Categories
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.category_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/product-variations"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <ViewCarouselIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Product Variations
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.product_variations_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/discover-listing"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <SearchIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Discovers
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.discover_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/welfare-listing"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <VolunteerActivismIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Welfares
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.welfare_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link to="/admin/emoji" className="dashboard-card-text-link">
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <EmojiEmotionsIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Emoji
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.emoji_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/app-notifications"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <NotificationsIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          App Notification
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.app_notification_count || 0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-xl-4 col-md-6 col-sm-4 col-12">
              <Link
                to="/admin/push-notifications"
                className="dashboard-card-text-link"
              >
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-pareto-theme-black">
                        <CircleNotificationsIcon fontSize="30px" />
                      </span>
                      <div className="dash-count">
                        <div className="dash-title dashboard-card-text-link">
                          Push Notification
                        </div>
                        <div className="dash-counts input-label">
                          <td className="h5">
                            {countersLoading
                              ? "Loading..."
                              : counters?.data?.firebase_notification_count ||
                              0}
                          </td>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div> */}

          </div>
          <div className="row">
            <div className="col-12 col-xl-12 col-md-12 col-sm-12">
              <div className="card flex-fill border-card mb-3">
                <div className="card-header dashboard-chat-heading p-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title text-black fw-bold fs-4 pl-2">Registered Users</h5>
                    <div className="d-flex align-items-center">
                      {analyticsPeriod === "custom" && (
                        <span
                          className="me-2 fw-bold"
                          style={{ color: "black", cursor: "pointer" }}
                          onClick={() => setAnchorEl(selectRef.current)}
                        >
                          {format(dateRange[0].startDate, "dd/MM/yyyy")} - {format(dateRange[0].endDate, "dd/MM/yyyy")}
                        </span>
                      )}
                      <Box sx={{ minWidth: 190 }}>
                        <FormControl fullWidth ref={selectRef}>
                          <Select
                            className="fw-bold fs-5 border border-2 border-white"
                            style={{ color: "black", borderRadius: "15px" }}
                            labelId="demo-simple-select-label"
                            value={analyticsPeriod}
                            onChange={handleChange}
                          >
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="week">This Week</MenuItem>
                            <MenuItem value="last_week">Last Week</MenuItem>
                            <MenuItem value="month">Monthly</MenuItem>
                            <MenuItem value="last_12_months">Last 12 Months</MenuItem>
                            <MenuItem value="year">Yearly</MenuItem>
                            <MenuItem value="custom">Custom Dates</MenuItem>
                          </Select>
                          <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClosePopover}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <Box
                              p={2}
                              sx={{
                                width: 'fit-content',
                                '& .rdrDateRangeWrapper': {
                                  width: 'auto',
                                },
                                '& .rdrCalendarWrapper': {
                                  width: 'auto',
                                  minWidth: 'unset',
                                },
                                '& .rdrMonths': {
                                  width: 'auto',
                                },
                                '& .rdrMonth': {
                                  width: 'auto',
                                },
                                '& .rdrDefinedRangesWrapper': {
                                  display: 'none',
                                },
                              }}
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-end"
                            >

                              <DateRange
                                editableDateInputs={true}
                                onChange={handleDateSelect}
                                moveRangeOnFirstSelection={false}
                                ranges={tempDateRange}
                                months={1}
                                rangeColors={['#AD5ADC']}
                                showDateDisplay={false}
                                maxDate={new Date()}
                              />
                              <Button
                                variant="contained"
                                onClick={handleApplyCustomDate}
                                sx={{
                                  mt: 2,
                                  backgroundColor: "#AD5ADC",
                                  "&:hover": { backgroundColor: "#8E44AD" },
                                  width: 'auto',
                                  borderRadius: '15px'
                                }}
                              >
                                Apply
                              </Button>
                            </Box>
                          </Popover>
                        </FormControl>
                      </Box>
                    </div>
                  </div>
                </div>
                <div className="card-body" style={{ height: '75vh' }}>
                  {registerUserChartLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="spinner-border loader-theme" role="status" />
                    </div>
                  ) : (
                    <Bar options={options} data={chartData} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-12 col-md-12 col-sm-12 table-width">
            <div className="table-width">
              <h2 className="page-title" style={{ color: "black" }}>Popular Discovers</h2>
              <div className="row">
                <div className="col-12 col-xl-12 col-md-12 col-sm-12">
                  <div className="custom-popularcard card-table mb-0">
                    <div className="table-responsive custom-pagination">
                      <table className="table align-middle" style={{ width: '100%' }}>
                        <thead className="dashboard-chat-heading text-white">
                          <tr style={{ color: "black" }}>
                            <th className="text-start padding-left-5" style={{ width: '20%' }}>Icon</th>
                            <th className="text-start padding-left-5" style={{ width: '40%' }}>Title</th>
                            <th className="text-start padding-left-5" style={{ width: '40%' }}>Type</th>
                          </tr>
                        </thead>
                        <tbody style={{ paddingTop: '20px' }}>
                          {discoverLoading && (
                            <tr>
                              <td align="center" colSpan={3}>
                                <div className="d-flex justify-content-center align-items-center py-4">
                                  <div className="spinner-border loader-theme" role="status" />
                                </div>
                              </td>
                            </tr>
                          )}

                          {!discoverLoading && popularDiscovers !== undefined && (
                            <>
                              {popularDiscovers?.data?.map((item, index) => {
                                return (
                                  <tr key={index} style={{ paddingTop: '10px' }}>
                                    <td className="text-start padding-left-5" style={{ width: '20%' }}>
                                      <img
                                        width={50}
                                        height={50}
                                        alt={item.name}
                                        src={item?.icon}
                                        onError={handleImageError}
                                        className="rounded"
                                      />
                                    </td>
                                    <td className="text-start padding-left-5" style={{ width: '40%' }}>{item?.title}</td>
                                    <td className="text-start text-capitalize padding-left-5" style={{ width: '40%' }}>
                                      {item?.type?.replace("_", " ") || "A - Z"}
                                    </td>
                                  </tr>
                                );
                              })}

                              {popularDiscovers?.data?.length === 0 && (
                                <tr>
                                  <td align="center" colSpan={3}>
                                    No Records Found.
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
